import { Check, Circle } from "lucide-react";
import { STEPS } from "@/lib/enrollment/stepsConfig";

export default function Sidebar({ currentStep, onJump, completedSteps, paymentStatus }) {
  return (
    <aside
      className="hidden md:flex md:flex-col w-72 lg:w-80 h-screen sticky top-0 bg-[#1B3B2B] text-white overflow-y-auto sidebar-scroll"
      data-testid="enrollment-sidebar"
    >
      <div className="px-8 pt-10 pb-8 border-b border-white/10">
        <div className="text-[10px] tracking-[0.35em] uppercase text-[#CD7F32] font-semibold">
          The
        </div>
        <div className="font-serif-display text-3xl lg:text-4xl leading-none mt-1">
          Scholar Athlete Academy
        </div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#8BA596] mt-3">
          Enrollment Portal · Class of 2026
        </div>
      </div>

      <nav className="flex-1 px-3 py-6 space-y-0.5" aria-label="Enrollment steps">
        {STEPS.map((step, idx) => {
          const isActive = idx === currentStep;
          const isComplete = completedSteps.has(idx) && !isActive;
          const isTuition = step.id === "tuition";
          const tuitionPaid = paymentStatus === "paid";
          const tuitionComped = paymentStatus === "comped";
          const showPayBadge = isTuition && !tuitionPaid && !tuitionComped;
          return (
            <button
              key={step.id}
              onClick={() => onJump(idx)}
              data-testid={`sidebar-step-${step.id}`}
              className={[
                "w-full text-left flex items-start gap-3 px-5 py-3 transition-all border-l-2",
                isActive
                  ? "border-[#CD7F32] bg-white/5 text-white"
                  : isComplete
                    ? "border-transparent text-[#CD7F32] hover:bg-white/5"
                    : "border-transparent text-[#8BA596] hover:text-white hover:bg-white/5",
              ].join(" ")}
            >
              <span className="flex-shrink-0 mt-0.5">
                {isComplete && !showPayBadge ? (
                  <Check size={16} strokeWidth={2.5} />
                ) : isActive ? (
                  <Circle size={10} strokeWidth={3} fill="#CD7F32" stroke="#CD7F32" />
                ) : (
                  <Circle size={10} strokeWidth={2} />
                )}
              </span>
              <span className="flex-1">
                <span className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] tabular-nums opacity-60">{String(idx + 1).padStart(2, "0")}</span>
                  <span className={isActive ? "font-semibold" : "font-medium"}>{step.title}</span>
                  {showPayBadge && (
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 bg-[#991B1B] text-white text-[9px] uppercase tracking-widest rounded-sm font-bold"
                      data-testid="sidebar-pay-required-badge"
                    >
                      Pay Required
                    </span>
                  )}
                  {isTuition && tuitionPaid && (
                    <span className="inline-flex items-center px-1.5 py-0.5 bg-[#CD7F32] text-white text-[9px] uppercase tracking-widest rounded-sm font-bold">
                      Paid
                    </span>
                  )}
                  {isTuition && tuitionComped && (
                    <span
                      className="inline-flex items-center px-1.5 py-0.5 bg-emerald-700 text-white text-[9px] uppercase tracking-widest rounded-sm font-bold"
                      data-testid="sidebar-fee-comped-badge"
                    >
                      Fee Covered
                    </span>
                  )}
                </span>
                <span className="block text-[11px] opacity-60 mt-0.5">{step.subtitle}</span>
              </span>
            </button>
          );
        })}
      </nav>

      <div className="px-8 py-6 border-t border-white/10 text-[11px] text-[#8BA596]">
        Progress saved automatically.
      </div>
    </aside>
  );
}

export function MobileProgressBar({ currentStep }) {
  const pct = ((currentStep + 1) / STEPS.length) * 100;
  return (
    <div
      className="md:hidden bg-[#1B3B2B] text-white px-5 py-4 sticky top-0 z-30"
      data-testid="mobile-progress"
    >
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[9px] tracking-[0.3em] uppercase text-[#CD7F32]">Scholar Athlete Academy</div>
          <div className="font-serif-display text-xl leading-tight mt-0.5">{STEPS[currentStep]?.title}</div>
        </div>
        <div className="text-[10px] tracking-widest tabular-nums">
          {String(currentStep + 1).padStart(2, "0")}/{String(STEPS.length).padStart(2, "0")}
        </div>
      </div>
      <div className="mt-3 h-[2px] bg-white/10 overflow-hidden">
        <div
          className="h-full bg-[#CD7F32] transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
